// Pure analysis: given a track's synced lyric lines (as returned by
// lyrics-client.js - [{time, text}], one entry per sung line, no explicit
// end time) and the track's own title, finds up to the 3 most-repeated
// short word-sets (1-3 words) to drive the permanent lyric-flash
// background effect - "choisi les 3 jeux de 1 a 3 mots les plus
// repetes [...] pas juste la sequence la plus repetee" - plus the time
// windows each one should flash on screen for. Never touches rendering/
// DOM - kept isolated so the scoring logic here is easy to reason about
// (and unit-test) on its own.
//
// No stopword list: a hardcoded list would need per-song language
// detection to be safe (this app sees French and English tracks alike),
// and would risk excluding a genuinely single-word hook ("SIA! SIA!"-
// style refrains exist). Instead this leans on the occurrence threshold
// itself (higher for a single word than for a 2-3 word phrase) plus a
// length-weighted score - a short phrase repeating identically several
// times is almost always an intentional chorus/refrain, while an
// incidental common word is unlikely to clear the higher single-word bar
// across a typical song's line count.

// Longer phrases are rarer by chance, so need fewer repeats to count as
// a real "hook" - and win ties against a same-count single word, since a
// multi-word repeat is a much stronger signal than a common word
// recurring.
const MIN_OCCURRENCES_BY_LENGTH = { 1: 6, 2: 4, 3: 3 };
const LENGTH_WEIGHT = { 1: 1, 2: 1.6, 3: 2.2 };
const MAX_HOOKS = 3;
// A candidate sharing most of its own occurrences with an already-picked
// phrase is very likely just a different word-window slice of the exact
// same repeated line ("hold on tight" and "on tight now" from one
// repeated "hold on tight now" line) rather than a genuinely distinct
// hook - skipped in favor of the next-best, less redundant candidate, so
// the 3 picks actually represent 3 different repeated moments.
const OVERLAP_SKIP_FRACTION = 0.6;

// Per-occurrence on-screen display window, derived from this line's
// start time up to (just before) the next line's start - capped both
// ways so a long instrumental gap doesn't leave text flashing for
// unrealistically long, and two lines sung back-to-back still get a
// readable minimum.
const MAX_OCCURRENCE_DURATION = 6;
const MIN_OCCURRENCE_DURATION = 1.2;
const GAP_BEFORE_NEXT_LINE = 0.15;

function normalizeWord(word) {
  return word.toLowerCase().replace(/[^\p{L}\p{N}']/gu, '');
}

function tokenizeLine(text) {
  return (text || '').split(/\s+/).map(normalizeWord).filter(Boolean);
}

function computeOccurrenceEnd(lines, index) {
  const start = lines[index].time;
  const next = lines[index + 1] ? lines[index + 1].time : start + MAX_OCCURRENCE_DURATION;
  let end = Math.min(next - GAP_BEFORE_NEXT_LINE, start + MAX_OCCURRENCE_DURATION);
  if (end - start < MIN_OCCURRENCE_DURATION) end = start + MIN_OCCURRENCE_DURATION;
  return end;
}

// True if gramWords appears as a contiguous run within titleWords -
// "si l'un [des jeux de mots repetes] correspond au titre de la chanson
// [...] prend celui-la": a candidate matching the title is a much
// stronger, more meaningful pick than an equally-repeated but
// title-unrelated phrase, so title matches are sorted first below,
// ahead of pure repeat-count score.
function titleContainsGram(titleWords, gramWords) {
  for (let start = 0; start + gramWords.length <= titleWords.length; start++) {
    let match = true;
    for (let k = 0; k < gramWords.length; k++) {
      if (titleWords[start + k] !== gramWords[k]) { match = false; break; }
    }
    if (match) return true;
  }
  return false;
}

// [{ phrase: "UPPERCASE TEXT", occurrences: [{time, endTime}, ...] }, ...]
// - up to MAX_HOOKS entries, most-repeated/title-matching first; empty
// array if nothing repeats often enough to bother with.
export function findRepeatedPhrases(lines, title) {
  if (!Array.isArray(lines) || lines.length === 0) return [];

  // gram -> { occurrences: [{time, endTime}] }. Each sliding-window match
  // increments this gram's effective repeat count; occurrences collapses
  // same-line repeats to a single display window, since only line-level
  // timing is available - flashing the same short window three times in
  // a row would just look glitchy.
  const stats = new Map();

  for (let i = 0; i < lines.length; i++) {
    const words = tokenizeLine(lines[i].text);
    if (words.length === 0) continue;
    const endTime = computeOccurrenceEnd(lines, i);

    for (let n = 1; n <= 3; n++) {
      for (let start = 0; start + n <= words.length; start++) {
        const gram = words.slice(start, start + n).join(' ');
        let entry = stats.get(gram);
        if (!entry) {
          entry = { occurrences: [] };
          stats.set(gram, entry);
        }
        const last = entry.occurrences[entry.occurrences.length - 1];
        if (!last || last.time !== lines[i].time) {
          entry.occurrences.push({ time: lines[i].time, endTime: endTime });
        }
      }
    }
  }

  const titleWords = title ? tokenizeLine(title) : [];

  const candidates = [];
  for (const [gram, entry] of stats) {
    const length = gram.split(' ').length;
    const minRequired = MIN_OCCURRENCES_BY_LENGTH[length] || Infinity;
    if (entry.occurrences.length < minRequired) continue;
    candidates.push({
      phrase: gram,
      occurrences: entry.occurrences,
      score: entry.occurrences.length * (LENGTH_WEIGHT[length] || 1),
      matchesTitle: titleWords.length > 0 && titleContainsGram(titleWords, gram.split(' '))
    });
  }

  candidates.sort(function (a, b) {
    if (a.matchesTitle !== b.matchesTitle) return a.matchesTitle ? -1 : 1;
    return b.score - a.score;
  });

  const selected = [];
  for (let i = 0; i < candidates.length && selected.length < MAX_HOOKS; i++) {
    const candidate = candidates[i];
    const ownTimes = candidate.occurrences.map(function (o) { return o.time; });
    const alreadyCovered = ownTimes.filter(function (t) {
      return selected.some(function (s) {
        return s.occurrences.some(function (o) { return o.time === t; });
      });
    }).length;
    if (alreadyCovered / ownTimes.length > OVERLAP_SKIP_FRACTION) continue;
    selected.push({ phrase: candidate.phrase.toUpperCase(), occurrences: candidate.occurrences });
  }

  return selected;
}
